// The honest seeded / snapshot / skipped breakdown of a pack install
// (Module 02 §3.2, §4 S2 "progress panel streaming seeded_counts").
//
// The one rule this component exists to enforce: a SKIPPED kind — one whose
// owning module is not wired into this deployment — is never drawn as
// installed. It renders in its own section, with its own explanation, showing
// "Pending <module>" in the created column. §3.2 calls a fake seed out by name;
// so does the UI.
//
// `owner_module` is server DATA (the install response names the module the seed
// waits on, e.g. "quality (Module 07)"), interpolated into a translated
// sentence exactly like a product name would be — the sentence is i18n, the
// value is the API's.

import { enumLabel } from "../../../../lib/enum-label";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { groupKinds, installTotals, type KindRow, type SeedDisposition } from "./install-model";

/**
 * UX-6b: the breakdown on `DataTable` (D-219 — computed columns are `render`
 * props, not a reason for a raw table). The certified walkthrough API is the
 * per-row `data-kind` attribute (M02-S01/S02 assert `tr[data-kind="…"]`), which
 * rides `rowProps`; the §4 phone collapse is now the primitive's own.
 */
function KindTable({
  rows,
  showCreated,
  pendingColumn,
}: {
  rows: readonly KindRow[];
  /** Seeded kinds show a real created/expected count. */
  showCreated: boolean;
  /** Skipped kinds show "Pending <module>" instead of a number. */
  pendingColumn: boolean;
}) {
  const { t } = useTranslation();

  const columns: DataTableColumn<KindRow>[] = [
    {
      key: "kind",
      header: t("onboarding.vertical.breakdown.colKind"),
      render: (row) => enumLabel(t, "onboarding.vertical.kinds", row.objectKind),
    },
    {
      key: "inPack",
      header: t("onboarding.vertical.breakdown.colInPack"),
      render: (row) => row.packCount,
    },
    {
      key: "created",
      header: t("onboarding.vertical.breakdown.colCreated"),
      render: (row) =>
        pendingColumn ? (
          <span className="vp-pending">
            {t("onboarding.vertical.breakdown.pendingModule", { module: row.ownerModule })}
          </span>
        ) : showCreated ? (
          t("onboarding.vertical.breakdown.created", {
            done: row.materialized ?? 0,
            total: row.packCount,
          })
        ) : (
          <span className="vp-muted">{t("onboarding.vertical.breakdown.inSnapshot")}</span>
        ),
    },
    {
      key: "owner",
      header: t("onboarding.vertical.breakdown.colOwner"),
      render: (row) => <span className="vp-muted">{row.ownerModule}</span>,
    },
  ];

  return (
    <div className="vp-table-wrap">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.objectKind}
        // The caller (`Group`) already returns null on an empty set, so this
        // branch is unreachable; the prop is the primitive's contract.
        empty={null}
        rowProps={(row) => ({ "data-kind": row.objectKind })}
      />
    </div>
  );
}

function Group({
  disposition,
  rows,
  headingKey,
  noteKey,
}: {
  disposition: SeedDisposition;
  rows: readonly KindRow[];
  headingKey: string;
  noteKey: string;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <section className="vp-breakdown-group" data-disposition={disposition}>
      <h3>{t(headingKey)}</h3>
      <p className="vp-breakdown-note">{t(noteKey)}</p>
      <KindTable
        rows={rows}
        showCreated={disposition === "seeded"}
        pendingColumn={disposition === "skipped"}
      />
    </section>
  );
}

export function InstallBreakdown({ rows }: { rows: readonly KindRow[] }) {
  const { t } = useTranslation();
  const groups = groupKinds(rows);
  const totals = installTotals(rows);

  return (
    <div className="vp-breakdown" data-testid="install-breakdown">
      <p className="vp-note">
        {t("onboarding.vertical.breakdown.totals", {
          materialized: totals.materialized,
          snapshot: totals.snapshot,
          pending: totals.pending,
        })}
      </p>
      <Group
        disposition="seeded"
        rows={groups.seeded}
        headingKey="onboarding.vertical.breakdown.seededHeading"
        noteKey="onboarding.vertical.breakdown.seededNote"
      />
      <Group
        disposition="snapshot"
        rows={groups.snapshot}
        headingKey="onboarding.vertical.breakdown.snapshotHeading"
        noteKey="onboarding.vertical.breakdown.snapshotNote"
      />
      <Group
        disposition="skipped"
        rows={groups.skipped}
        headingKey="onboarding.vertical.breakdown.skippedHeading"
        noteKey="onboarding.vertical.breakdown.skippedNote"
      />
    </div>
  );
}
